<template>
  <v-app id="inspire">
    <v-card-title>Users</v-card-title>

    <div class="search-bar">
      <v-text-field
        class="text-field"
        v-model="search"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
        clearable
      />
      <v-select
        v-model="selectedRoles"
        :items="roleOptions"
        label="Filter by role"
        multiple
        chips
        small-chips
        deletable-chips
        clearable
        hide-details
        class="role-filter ml-5"
      />
    </div>

    <v-data-table
      :headers="headers"
      :items="usersWithFullName"
      :loading="$apollo.queries.getUsers.loading"
      hide-default-footer
      disable-pagination
      item-key="id"
    >
      <template v-slot:item.accessControl.role="{ item }">
        <span class="role-cell">
          <v-icon v-if="roleIcon(item)" small class="role-icon">{{
            roleIcon(item)
          }}</v-icon>
          <span v-else class="role-icon-placeholder" />
          <span class="capitalize-first-letter">{{
            item.accessControl ? item.accessControl.role : ''
          }}</span>
        </span>
      </template>
      <template v-slot:item.country="{ item }">
        <span
          v-if="item.country"
          :class="`fi fi-${getCountryCode(item.country)}`"
          class="mr-2"
        />
        <span>{{ item.country }}</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn icon @click="openRoleDialog(item)">
          <v-icon>mdi-account-edit</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="showRoleDialog" max-width="400px">
      <v-card>
        <v-card-title>Assign Role</v-card-title>
        <v-card-text>
          <div class="mb-3" v-if="editingUser">
            {{ editingUser.fullName || editingUser.email }}
          </div>
          <v-select
            v-model="newRole"
            :items="assignableRoles"
            label="Role"
            outlined
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showRoleDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="saveRole(editingUser.id)">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="pagination-controls">
      <v-btn :disabled="currentPage === 1" @click="prevPage" text>
        <v-icon left>mdi-chevron-left</v-icon> Previous
      </v-btn>
      <span class="page-label">Page {{ currentPage }}</span>
      <v-btn :disabled="getUsers.length < pageSize" @click="nextPage" text>
        Next <v-icon right>mdi-chevron-right</v-icon>
      </v-btn>
    </div>
  </v-app>
</template>

<script>
import 'flag-icons/css/flag-icons.min.css';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

countries.registerLocale(enLocale);

import {
  GET_USERS,
  UPDATE_USER_ACCESS_CONTROL,
} from '../../graphql/authenticationQueries';

export default {
  data() {
    return {
      search: '',
      searchValue: '',
      searchTimeout: null,
      currentPage: 1,
      pageSize: 50,
      getUsers: [],
      showRoleDialog: false,
      editingUser: null,
      newRole: '',
      assignableRoles: [
        { text: 'User', value: 'user' },
        { text: 'Sales', value: 'sales' },
        { text: 'Support', value: 'support' },
        { text: 'System', value: 'system' },
      ],
      selectedRoles: [],
      roleOptions: [
        { text: 'Sales', value: 'sales' },
        { text: 'Support', value: 'support' },
        { text: 'System', value: 'system' },
      ],
      headers: [
        {
          text: 'Full Name',
          value: 'fullName',
          align: 'left',
          sortable: false,
        },
        { text: 'Email', value: 'email', align: 'left', sortable: false },
        {
          text: 'Role',
          value: 'accessControl.role',
          align: 'left',
          sortable: false,
        },
        { text: 'Country', value: 'country', align: 'left', sortable: false },
        { text: 'Actions', value: 'actions', align: 'right', sortable: false },
      ],
    };
  },
  apollo: {
    $client: 'authenticationClient',
    getUsers: {
      query: GET_USERS,
      variables() {
        return {
          search: this.searchValue || null,
          roleFilter: this.selectedRoles,
          limit: this.pageSize,
          offset: (this.currentPage - 1) * this.pageSize,
        };
      },
      update: (data) => data.getUsers,
      loadingKey: 'loading',
    },
  },
  computed: {
    usersWithFullName() {
      return this.getUsers.map((u) => ({
        ...u,
        fullName: [u.firstName, u.lastName].filter(Boolean).join(' '),
      }));
    },
  },
  watch: {
    search(val) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.searchValue = val || '';
      }, 400);
    },
  },
  methods: {
    getCountryCode(country) {
      if (!country) return '';
      const normalized = String(country).trim();

      // Already country code format:  NO, SE, US
      if (/^[a-z]{2}$/i.test(normalized)) return normalized.toLowerCase();

      // If country name, e.g. Norway, format to alpha2 code
      return countries.getAlpha2Code(normalized, 'en')?.toLowerCase() || '';
    },
    openRoleDialog(user) {
      this.editingUser = user;
      this.newRole = user.accessControl?.role?.toLowerCase() || '';
      this.showRoleDialog = true;
    },
    async saveRole(userId) {
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_USER_ACCESS_CONTROL,
          variables: {
            userId: userId,
            role: this.newRole,
            resource: this.editingUser.accessControl?.resource || '*',
          },
        });
        this.showRoleDialog = false;
        this.$apollo.queries.getUsers.refetch();
      } catch (e) {
        alert('Failed to update role: ' + e.message);
      }
    },
    roleIcon(item) {
      const role = item.accessControl?.role?.toLowerCase();
      const icons = {
        user: 'mdi-account-outline',
        sales: 'mdi-cash-multiple',
        support: 'mdi-headset',
        system: 'mdi-cog',
      };
      return icons[role] || null;
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.getUsers.length >= this.pageSize) {
        this.currentPage++;
      }
    },
  },
};
</script>

<style scoped>
.search-bar {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  padding: 0 16px 12px;
}

.text-field {
  flex: 0 0 300px;
}

.role-filter {
  flex: 0 0 400px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
  gap: 16px;
}

.page-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}

.role-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.role-icon-placeholder {
  display: inline-block;
  width: 16px;
}

.capitalize-first-letter:first-letter {
  text-transform: uppercase;
}
</style>
